// Small chooser shown when a card is dropped on Done and the workflow has
// several terminal states: which one does the task end in?

import { useId, useState } from 'react'
import type { BoardColumn } from '@goblic/shared'

type TerminalState = NonNullable<BoardColumn['terminalStates']>[number]

const OUTCOME_LABEL: Record<TerminalState['outcome'], string> = {
  success: 'success',
  failure: 'failure',
  cancelled: 'cancelled',
}

export function DoneChooser({
  taskLabel,
  states,
  onChoose,
  onCancel,
}: {
  taskLabel: string
  states: NonNullable<BoardColumn['terminalStates']>
  onChoose: (stateName: string) => void
  onCancel: () => void
}) {
  const titleId = useId()
  const [picked, setPicked] = useState(states[0]?.name ?? '')
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-ink/40" onClick={onCancel} aria-hidden />
      <form
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid="done-chooser"
        className="relative w-full max-w-sm rounded-[16px] border border-line bg-surface p-5 shadow-overlay"
        onSubmit={(event) => {
          event.preventDefault()
          if (picked) onChoose(picked)
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') onCancel()
        }}
      >
        <h2 id={titleId} className="font-display text-lg font-semibold text-ink">
          How did it end?
        </h2>
        <p className="mt-1 text-base text-ink-2">
          {taskLabel} moves to Done. Pick the state it ends in; a running step is cancelled.
        </p>
        <fieldset className="mt-3 space-y-1.5">
          <legend className="sr-only">End state</legend>
          {states.map((state) => (
            <label
              key={state.name}
              className={`flex cursor-pointer items-center justify-between gap-3 rounded-control border px-3 py-2 text-base ${
                picked === state.name ? 'border-moss bg-moss-soft text-moss-strong' : 'border-line-strong text-ink-2 hover:bg-sunken'
              }`}
            >
              <span className="flex items-center gap-2">
                <input
                  type="radio"
                  name="done-state"
                  value={state.name}
                  checked={picked === state.name}
                  onChange={() => setPicked(state.name)}
                />
                {state.name}
              </span>
              <span className="text-sm text-ink-3">{OUTCOME_LABEL[state.outcome]}</span>
            </label>
          ))}
        </fieldset>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" className="btn-ghost" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={!picked}>
            Move to Done
          </button>
        </div>
      </form>
    </div>
  )
}
